/**
 * Hallyu4U Complete Enterprise System
 * All delivered.co.kr features integrated
 * Version: 3.0.0 - Production Ready
 */

class Hallyu4UCompleteSystem {
  constructor() {
    this.version = "3.0.0";
    this.initialized = false;

    // System modules
    this.modules = {
      backend: null,
      warehouse: null,
      shipping: null,
      tracking: null,
      notification: null,
      points: null,
      language: null,
    };

    // User data
    this.user = {
      id: null,
      email: null,
      name: null,
      koreanAddress: null,
      points: 0,
      tier: "bronze",
    };

    // Initialize on DOM ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }
  }

  async init() {
    try {
      await this.initializeUser();
      await this.initializeModules();
      await this.setupUI();
      await this.loadData();

      this.initialized = true;

      // Auto-detect and setup variants
      // await this.autoSetupVariants();
    } catch (error) {
      console.error("System initialization failed:", error);
      this.handleSystemError(error);
    }
  }

  // ===============================
  // USER MANAGEMENT
  // ===============================

  async initializeUser() {
    const customerData = window.HALLYU4U_CONFIG?.customer;

    if (customerData) {
      this.user = {
        id: customerData.id,
        email: customerData.email,
        name: `${customerData.first_name} ${customerData.last_name}`,
        // koreanAddress: this.generateKoreanAddress(customerData.id),
        points: 0,
        tier: "bronze",
      };

      // Load user data from storage
      await this.loadUserData();
    }
  }

//   generateKoreanAddress(customerId) {
//     return {
//       line1: `Customer #${customerId}`,
//       line2: "Hallyu4U Warehouse",
//       line3: "123-45 Gangnam-daero",
//       line4: "Gangnam-gu, Seoul 06234",
//       line5: "South Korea",
//       phone: "+82-2-1234-5678",
//       formatted: `Customer #${customerId}
// Hallyu4U Warehouse
// 123-45 Gangnam-daero
// Gangnam-gu, Seoul 06234
// South Korea
// Phone: +82-2-1234-5678`,
//     };
//   }

  async loadUserData() {
    const stored = localStorage.getItem("hallyu4u_user_data");
    if (stored) {
      const data = JSON.parse(stored);
      this.user.points = data.points || 0;
      this.user.tier = data.tier || "bronze";
    }
  }

  // ===============================
  // MODULE INITIALIZATION
  // ===============================

  async initializeModules() {
    // Backend module
    this.modules.backend = new BackendModule(this);

    // Warehouse module
    this.modules.warehouse = new WarehouseModule(this);

    // Shipping module
    this.modules.shipping = new ShippingModule(this);

    // Tracking module
    this.modules.tracking = new TrackingModule(this);

    // Notification module
    this.modules.notification = new NotificationModule(this);

    // Points module
    this.modules.points = new PointsModule(this);

    // Language module
    this.modules.language = new LanguageModule(this);
  }

  // ===============================
  // UI SETUP
  // ===============================

  async setupUI() {
    // Add global event listeners
    this.setupEventListeners();

    // Setup navigation
    this.setupNavigation();

    // Initialize Alpine.js components
    this.initializeAlpineComponents();

    // Setup notifications container
    this.setupNotifications();
  }

  setupEventListeners() {
    // Global package events
    document.addEventListener("package:add", (e) =>
      this.handlePackageAdd(e.detail)
    );
    document.addEventListener("package:ship", (e) =>
      this.handlePackageShip(e.detail)
    );
    document.addEventListener("package:track", (e) =>
      this.handlePackageTrack(e.detail)
    );

    // Copy address button
    document.addEventListener("click", (e) => {
      if (e.target.matches("[data-copy-address]")) {
        this.copyKoreanAddress();
      }
    });
  }

  setupNavigation() {
    // Check current page and setup appropriate features
    const path = window.location.pathname;

    if (path.includes("/account/dashboard")) {
      this.initDashboard();
    } else if (path.includes("/account/warehouse")) {
      this.initWarehouse();
    } else if (path.includes("/account/shipping")) {
      this.initShipping();
    } else if (path.includes("/account/tracking")) {
      this.initTracking();
    }
  }

  initializeAlpineComponents() {
    // Register Alpine.js components
    if (window.Alpine) {
      // Dashboard component
      window.Alpine.data("dashboard", () => ({
        stats: {
          totalPackages: 0,
          inStorage: 0,
          shipped: 0,
          delivered: 0,
        },
        init() {
          this.loadStats();
        },
        async loadStats() {
          const packages = await window.hallyu4u.getPackages();
          this.stats.totalPackages = packages.length;
          this.stats.inStorage = packages.filter(
            (p) => p.status === "storage"
          ).length;
          this.stats.shipped = packages.filter(
            (p) => p.status === "shipped"
          ).length;
          this.stats.delivered = packages.filter(
            (p) => p.status === "delivered"
          ).length;
        },
      }));

      // Warehouse component
      window.Alpine.data("warehouse", () => ({
        packages: [],
        filteredPackages: [],
        selectedPackages: new Set(),
        searchQuery: "",
        statusFilter: "",
        showAddModal: false,
        newPackage: {
          trackingNumber: "",
          sender: "",
          weight: "",
          invoiceNumber: "",
          contents: "",
        },

        init() {
          this.loadPackages();
        },

        async loadPackages() {
          this.packages = await window.hallyu4u.getPackages();
          this.filterPackages();
        },

        filterPackages() {
          let filtered = [...this.packages];

          if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(
              (p) =>
                p.trackingNumber.toLowerCase().includes(query) ||
                p.sender.toLowerCase().includes(query) ||
                p.invoiceNumber?.toLowerCase().includes(query)
            );
          }

          if (this.statusFilter) {
            filtered = filtered.filter((p) => p.status === this.statusFilter);
          }

          this.filteredPackages = filtered;
        },

        async addPackage() {
          const result = await window.hallyu4u.addPackage(this.newPackage);
          if (result.success) {
            this.showAddModal = false;
            this.newPackage = {
              trackingNumber: "",
              sender: "",
              weight: "",
              invoiceNumber: "",
              contents: "",
            };
            await this.loadPackages();
            window.hallyu4u.showNotification(
              "Package added successfully!",
              "success"
            );
          }
        },

        togglePackageSelection(packageId) {
          if (this.selectedPackages.has(packageId)) {
            this.selectedPackages.delete(packageId);
          } else {
            this.selectedPackages.add(packageId);
          }
        },

        async consolidateSelected() {
          const ids = Array.from(this.selectedPackages);
          if (ids.length < 2) {
            window.hallyu4u.showNotification(
              "Select at least 2 packages to consolidate",
              "warning"
            );
            return;
          }

          const result = await window.hallyu4u.consolidatePackages(ids);
          if (result.success) {
            this.selectedPackages.clear();
            await this.loadPackages();
            window.hallyu4u.showNotification(
              `Packages consolidated! Saved ${result.saved}`,
              "success"
            );
          }
        },

        requestShipping() {
          const ids = Array.from(this.selectedPackages);
          if (ids.length === 0) {
            window.hallyu4u.showNotification(
              "Select packages to ship",
              "warning"
            );
            return;
          }

          window.location.href = `/account/shipping?packages=${ids.join(",")}`;
        },
      }));
    }
  }

  setupNotifications() {
    if (!document.getElementById("notification-container")) {
      const container = document.createElement("div");
      container.id = "notification-container";
      container.className = "fixed top-4 right-4 z-50 space-y-2";
      document.body.appendChild(container);
    }
  }

  // ===============================
  // DATA LOADING
  // ===============================

  async loadData() {
    // Load packages
    await this.loadPackages();

    // Load points
    await this.loadPoints();
  }

  async loadPackages() {
    try {
      const packages = await this.modules.backend.getPackages();
      this.packages = packages;
      return packages;
    } catch (error) {
      console.error("Failed to load packages:", error);
      return [];
    }
  }

  async loadPoints() {
    try {
      const points = await this.modules.points.getBalance();
      this.user.points = points.balance;
      this.user.tier = points.tier;
      return points;
    } catch (error) {
      console.error("Failed to load points:", error);
      return { balance: 0, tier: "bronze" };
    }
  }

  // ===============================
  // AUTO VARIANT SETUP
  // ===============================

  // async autoSetupVariants() {
  //   try {
  //     const response = await axios.get("/products.json?limit=250");
  //     if (response.status === 200) {
  //       const data = response.data;
  //       const products = data.products || [];

  //       const variantMap = {
  //         shipping: {},
  //         services: {},
  //       };

  //       products.forEach((product) => {
  //         product.variants?.forEach((variant) => {
  //           const title = product.title.toLowerCase();
  //           const sku = (variant.sku || "").toLowerCase();
  //           const price = parseFloat(variant.price);

  //           // Detect shipping methods
  //           if (
  //             title.includes("k-packet") ||
  //             sku.includes("kpacket") ||
  //             price === 8.5
  //           ) {
  //             variantMap.shipping["k-packet"] = this.extractVariantId(
  //               variant.id
  //             );
  //           } else if (
  //             title.includes("ems") ||
  //             sku.includes("ems") ||
  //             price === 25.0
  //           ) {
  //             variantMap.shipping["ems"] = this.extractVariantId(variant.id);
  //           } else if (
  //             title.includes("dhl") ||
  //             sku.includes("dhl") ||
  //             price === 35.0
  //           ) {
  //             variantMap.shipping["dhl"] = this.extractVariantId(variant.id);
  //           } else if (
  //             title.includes("surface") ||
  //             sku.includes("surface") ||
  //             price === 15.0
  //           ) {
  //             variantMap.shipping["surface"] = this.extractVariantId(
  //               variant.id
  //             );
  //           }

  //           // Detect services
  //           else if (
  //             title.includes("repack") ||
  //             sku.includes("repack") ||
  //             price === 3.0
  //           ) {
  //             variantMap.services["repackaging"] = this.extractVariantId(
  //               variant.id
  //             );
  //           } else if (
  //             title.includes("photo") ||
  //             sku.includes("photo") ||
  //             price === 2.0
  //           ) {
  //             variantMap.services["photos"] = this.extractVariantId(variant.id);
  //           }
  //         });
  //       });

  //       // Save variant map
  //       this.variantMap = variantMap;
  //       localStorage.setItem("hallyu4u_variants", JSON.stringify(variantMap));
  //     }
  //   } catch (error) {
  //     console.log("Using default variant configuration");
  //   }
  // }

  extractVariantId(id) {
    // Handle GraphQL ID format
    if (typeof id === "string" && id.includes("gid://")) {
      const match = id.match(/(\d+)$/);
      return match ? match[1] : id;
    }
    return id?.toString() || "";
  }

  // ===============================
  // CORE FEATURES
  // ===============================

  async getPackages(filters = {}) {
    return this.modules.backend.getPackages(filters);
  }

  async addPackage(packageData) {
    try {
      const pkg = await this.modules.backend.savePackage(packageData);

      // Send notification
      this.showNotification("New package registered successfully!", "success");

      // Earn points
      await this.modules.points.earn(10, "package_registered", pkg.id);

      return { success: true, packageData: pkg };
    } catch (error) {
      this.showNotification("Failed to add package", "error");
      return { success: false, error };
    }
  }

  async consolidatePackages(packageIds) {
    try {
      const result = await this.modules.backend.consolidatePackages(packageIds);

      // Calculate savings
      const saved = `$${result.savings.saved.toFixed(2)} (${
        result.savings.percentage
      }%)`;

      // Earn bonus points
      await this.modules.points.earn(50, "consolidation_bonus", result.id);

      return { success: true, saved, result };
    } catch (error) {
      return { success: false, error };
    }
  }

  async calculateShipping(packageIds, destination, method) {
    return this.modules.shipping.calculate(packageIds, destination, method);
  }

  async requestShipping(shippingData) {
    try {
      const request = await this.modules.shipping.createRequest(shippingData);

      // Send confirmation email
      await this.modules.notification.send("shipping_requested", {
        orderId: request.id,
        method: shippingData.method,
        destination: shippingData.destination,
      });

      // Earn points
      await this.modules.points.earn(100, "shipping_requested", request.id);

      return { success: true, request };
    } catch (error) {
      return { success: false, error };
    }
  }

  async trackPackage(trackingNumber) {
    return this.modules.tracking.track(trackingNumber);
  }

  // ===============================
  // UI HELPERS
  // ===============================

  copyKoreanAddress() {
    if (this.user.koreanAddress) {
      navigator.clipboard
        .writeText(this.user.koreanAddress.formatted)
        .then(() => {
          this.showNotification("Address copied to clipboard!", "success");
        });
    }
  }

  showNotification(message, type = "info") {
    const container = document.getElementById("notification-container");
    if (!container) return;

    const notification = document.createElement("div");
    notification.className = `notification ${type} p-4 rounded-lg shadow-lg max-w-sm animate-slide-in`;

    const colors = {
      success: "bg-green-500 text-white",
      error: "bg-red-500 text-white",
      warning: "bg-yellow-500 text-white",
      info: "bg-blue-500 text-white",
    };

    notification.className += ` ${colors[type] || colors.info}`;
    notification.textContent = message;

    container.appendChild(notification);

    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.classList.add("animate-fade-out");
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  // ===============================
  // PAGE INITIALIZERS
  // ===============================

  initDashboard() {
    console.log("Initializing dashboard...");
    // Dashboard specific initialization
  }

  initWarehouse() {
    console.log("Initializing warehouse...");
    // Warehouse specific initialization
  }

  initShipping() {
    console.log("Initializing shipping calculator...");
    // Shipping specific initialization
  }

  initTracking() {
    console.log("Initializing tracking...");
    // Tracking specific initialization
  }

  // ===============================
  // ERROR HANDLING
  // ===============================

  handleSystemError(error) {
    console.error("System error:", error);

    // Try to recover
    setTimeout(() => {
      this.init();
    }, 5000);
  }

  // ===============================
  // PUBLIC API
  // ===============================

  getVersion() {
    return this.version;
  }

  isReady() {
    return this.initialized;
  }

  getUser() {
    return this.user;
  }
}

// ===============================
// BACKEND MODULE
// ===============================

class BackendModule {
  constructor(system) {
    this.system = system;
    this.cache = new Map();
    this.variantIds = {};
  }

  async getPackages(filters = {}) {
    // Check cache
    const cacheKey = JSON.stringify(filters);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Load from storage
    let packages = JSON.parse(
      localStorage.getItem("hallyu4u_packages") || "[]"
    );

    // Apply filters
    if (filters.status) {
      packages = packages.filter((p) => p.status === filters.status);
    }

    // Cache result
    this.cache.set(cacheKey, packages);

    return packages;
  }

  async savePackage(packageData) {
    const pkg = {
      ...packageData,
      id: `PKG${Date.now()}${Math.random().toString(36).substr(2, 9)}`,
      status: "pending_verification"
    };

    // Save to storage
    const packages = await this.getPackages();
    packages.push(pkg);
    localStorage.setItem("hallyu4u_packages", JSON.stringify(packages));

    // Clear cache
    this.cache.clear();

    return pkg;
  }

  async updatePackage(packageId, updates) {
    const packages = await this.getPackages();
    const index = packages.findIndex((p) => p.id === packageId);

    if (index > -1) {
      packages[index] = { ...packages[index], ...updates };
      localStorage.setItem("hallyu4u_packages", JSON.stringify(packages));
      this.cache.clear();
      return packages[index];
    }

    throw new Error("Package not found");
  }

  async consolidatePackages(packageIds) {
    const packages = await this.getPackages();
    const toConsolidate = packages.filter((p) => packageIds.includes(p.id));

    if (toConsolidate.length < 2) {
      throw new Error("At least 2 packages required");
    }

    // Calculate combined weight
    const totalWeight = toConsolidate.reduce(
      (sum, p) => sum + parseFloat(p.weight || 0),
      0
    );

    // Create consolidated package
    const consolidated = {
      id: `CON${Date.now()}`,
      type: "consolidated",
      originalPackages: packageIds,
      weight: totalWeight,
      status: "storage",
    };

    // Update original packages
    for (const id of packageIds) {
      await this.updatePackage(id, {
        status: "consolidated",
        consolidatedId: consolidated.id,
      });
    }

    // Save consolidated package
    packages.push(consolidated);
    localStorage.setItem("hallyu4u_packages", JSON.stringify(packages));
    this.cache.clear();

    // Calculate savings
    const individualCost = toConsolidate.reduce(
      (sum, p) => sum + this.calculateShipping(p.weight),
      0
    );
    const consolidatedCost = this.calculateShipping(totalWeight);
    const saved = individualCost - consolidatedCost;
    const percentage = ((saved / individualCost) * 100).toFixed(1);

    return {
      consolidated,
      savings: { individualCost, consolidatedCost, saved, percentage },
    };
  }

  calculateShipping(weight) {
    // Simple tiered pricing
    if (weight <= 1) return 8.5;
    if (weight <= 5) return 8.5 + (weight - 1) * 6;
    if (weight <= 10) return 32.5 + (weight - 5) * 7;
    return 67.5 + (weight - 10) * 8;
  }

  async getVariantId(type, method) {
    // Auto-detect if not cached
    if (!this.variantIds[type]?.[method]) {
      const stored = localStorage.getItem("hallyu4u_variants");
      if (stored) {
        const variants = JSON.parse(stored);
        this.variantIds = variants;
      }
    }

    return this.variantIds[type]?.[method] || "41971139903550"; // Fallback ID
  }
}

// ===============================
// WAREHOUSE MODULE
// ===============================

class WarehouseModule {
  constructor(system) {
    this.system = system;
  }

  async getStorageStatus() {
    const packages = await this.system.modules.backend.getPackages({
      status: "storage",
    });
    const totalWeight = packages.reduce(
      (sum, p) => sum + parseFloat(p.weight || 0),
      0
    );
    const totalVolume = packages.length * 0.1; // Estimated volume

    return {
      packages: packages.length,
      weight: totalWeight,
      volume: totalVolume,
      capacity: {
        used: packages.length,
        total: 100,
        percentage: ((packages.length / 100) * 100).toFixed(1),
      },
    };
  }

  async requestPhoto(packageId, options = {}) {
    const photoRequest = {
      id: `PHO${Date.now()}`,
      packageId,
      photos: options.count || 5,
      angles: options.angles || ["front", "back", "sides", "label", "contents"],
      status: "pending"
    };

    // Save request
    const requests = JSON.parse(
      localStorage.getItem("hallyu4u_photo_requests") || "[]"
    );
    requests.push(photoRequest);
    localStorage.setItem("hallyu4u_photo_requests", JSON.stringify(requests));

    return photoRequest;
  }

  async requestRepackaging(packageId, options = {}) {
    const repackRequest = {
      id: `REP${Date.now()}`,
      packageId,
      type: options.type || "standard",
      removeInvoice: options.removeInvoice || false,
      addBubbleWrap: options.addBubbleWrap || false,
      status: "pending",
    };

    // Save request
    const requests = JSON.parse(
      localStorage.getItem("hallyu4u_repack_requests") || "[]"
    );
    requests.push(repackRequest);
    localStorage.setItem("hallyu4u_repack_requests", JSON.stringify(requests));

    return repackRequest;
  }
}

// ===============================
// SHIPPING MODULE
// ===============================

class ShippingModule {
  constructor(system) {
    this.system = system;
    this.methods = {
      "k-packet": {
        name: "K-Packet",
        base: 8.5,
        perKg: 6,
        maxWeight: 2,
        days: "7-15",
      },
      ems: { name: "EMS", base: 25, perKg: 12, maxWeight: 30, days: "3-7" },
      dhl: {
        name: "DHL Express",
        base: 35,
        perKg: 18,
        maxWeight: 70,
        days: "2-4",
      },
      surface: {
        name: "Surface Mail",
        base: 15,
        perKg: 3,
        maxWeight: 30,
        days: "30-60",
      },
    };
  }

  async calculate(packageIds, destination, method = null) {
    const packages = await this.system.modules.backend.getPackages();
    const selected = packages.filter((p) => packageIds.includes(p.id));
    const totalWeight = selected.reduce(
      (sum, p) => sum + parseFloat(p.weight || 0),
      0
    );

    const options = [];

    for (const [key, config] of Object.entries(this.methods)) {
      if (method && key !== method) continue;
      if (totalWeight > config.maxWeight) continue;

      const cost = config.base + totalWeight * config.perKg;
      const zoneMultiplier = this.getZoneMultiplier(destination);

      options.push({
        method: key,
        name: config.name,
        cost: cost * zoneMultiplier,
        deliveryDays: config.days,
        available: true,
      });
    }

    return options.sort((a, b) => a.cost - b.cost);
  }

  getZoneMultiplier(destination) {
    const zones = {
      JP: 1.0,
      CN: 1.0,
      TW: 1.0,
      US: 1.5,
      CA: 1.5,
      GB: 1.6,
      DE: 1.6,
      FR: 1.6,
      AU: 1.4,
      NZ: 1.4,
    };
    return zones[destination] || 1.5;
  }

  async createRequest(shippingData) {
    const request = {
      id: `SHP${Date.now()}`,
      ...shippingData,
      status: "pending_payment"
    };

    // Save request
    const requests = JSON.parse(
      localStorage.getItem("hallyu4u_shipping_requests") || "[]"
    );
    requests.push(request);
    localStorage.setItem(
      "hallyu4u_shipping_requests",
      JSON.stringify(requests)
    );

    // Update package statuses
    for (const packageId of shippingData.packages) {
      await this.system.modules.backend.updatePackage(packageId, {
        status: "requested",
      });
    }

    return request;
  }
}

// ===============================
// TRACKING MODULE
// ===============================

class TrackingModule {
  constructor(system) {
    this.system = system;
  }

  async track(trackingNumber) {
    // Check if it's our internal tracking
    const packages = await this.system.modules.backend.getPackages();
    const pkg = packages.find((p) => p.trackingNumber === trackingNumber);

    if (pkg) {
      return this.getInternalTracking(pkg);
    }

    // External tracking
    return this.getExternalTracking(trackingNumber);
  }

  getInternalTracking(pkg) {
    const events = [];
    const now = new Date();

    // Generate tracking events based on status
    if (pkg.createdAt) {
      events.push({
        date: pkg.createdAt,
        status: "Package registered",
        location: "Online",
        description: "Package information received",
      });
    }

    if (
      pkg.status === "storage" ||
      pkg.status === "shipped" ||
      pkg.status === "delivered"
    ) {
      events.push({
        date: new Date(
          new Date(pkg.createdAt).getTime() + 24 * 60 * 60 * 1000
        ).toISOString(),
        status: "Arrived at warehouse",
        location: "Seoul, KR",
        description: "Package received and verified",
      });
    }

    if (pkg.status === "shipped" || pkg.status === "delivered") {
      events.push({
        date: new Date(
          new Date(pkg.createdAt).getTime() + 3 * 24 * 60 * 60 * 1000
        ).toISOString(),
        status: "Shipped",
        location: "Seoul, KR",
        description: "Package dispatched from warehouse",
      });
    }

    if (pkg.status === "delivered") {
      events.push({
        date: new Date(
          new Date(pkg.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000
        ).toISOString(),
        status: "Delivered",
        location: "Destination",
        description: "Package delivered successfully",
      });
    }

    return {
      trackingNumber: pkg.trackingNumber,
      status: pkg.status,
      events: events.reverse(),
      currentLocation: events[0]?.location || "Unknown",
      estimatedDelivery: pkg.estimatedDelivery || "Pending",
    };
  }

  getExternalTracking(trackingNumber) {
    // Simulate external tracking
    return {
      trackingNumber,
      status: "in_transit",
      events: [
        {
          date: new Date().toISOString(),
          status: "In Transit",
          location: "Unknown",
          description: "Package in transit",
        },
      ],
      currentLocation: "In Transit",
      estimatedDelivery: "Calculating...",
    };
  }
}

// ===============================
// NOTIFICATION MODULE
// ===============================

class NotificationModule {
  constructor(system) {
    this.system = system;
    this.templates = {
      package_received: "New package {{trackingNumber}} has arrived",
      package_shipped: "Your package {{trackingNumber}} has been shipped",
      shipping_requested: "Shipping request {{orderId}} received",
      consolidation_complete: "Packages consolidated successfully",
    };
  }

  async send(type, data) {
    const template = this.templates[type];
    if (!template) return;

    // Parse template
    let message = template;
    for (const [key, value] of Object.entries(data)) {
      message = message.replace(`{{${key}}}`, value);
    }

    // Show notification
    this.system.showNotification(message, "info");

    // Save to history
    const history = JSON.parse(
      localStorage.getItem("hallyu4u_notifications") || "[]"
    );
    history.unshift({
      type,
      message,
      data,
      timestamp: new Date().toISOString(),
      read: false,
    });

    // Keep only last 50
    if (history.length > 50) {
      history.pop();
    }

    localStorage.setItem("hallyu4u_notifications", JSON.stringify(history));
  }

  async getUnread() {
    const history = JSON.parse(
      localStorage.getItem("hallyu4u_notifications") || "[]"
    );
    return history.filter((n) => !n.read);
  }

  async markAsRead(notificationId) {
    const history = JSON.parse(
      localStorage.getItem("hallyu4u_notifications") || "[]"
    );
    const notification = history.find((n) => n.timestamp === notificationId);
    if (notification) {
      notification.read = true;
      localStorage.setItem("hallyu4u_notifications", JSON.stringify(history));
    }
  }
}

// ===============================
// POINTS MODULE
// ===============================

class PointsModule {
  constructor(system) {
    this.system = system;
    this.pointRates = {
      package_registered: 10,
      shipping_requested: 100,
      consolidation_bonus: 50,
      review_submitted: 20,
      referral: 500,
    };
  }

  async getBalance() {
    const data = JSON.parse(localStorage.getItem("hallyu4u_points") || "{}");
    return {
      balance: data.balance || 0,
      lifetime: data.lifetime || 0,
      tier: this.calculateTier(data.lifetime || 0),
      history: data.history || [],
    };
  }

  async earn(points, reason, reference = null) {
    const data = await this.getBalance();

    data.balance += points;
    data.lifetime += points;
    data.tier = this.calculateTier(data.lifetime);

    // Add to history
    data.history = data.history || [];
    data.history.unshift({
      type: "earned",
      points,
      reason,
      reference,
      timestamp: new Date().toISOString(),
    });

    // Keep only last 100
    if (data.history.length > 100) {
      data.history.pop();
    }

    localStorage.setItem("hallyu4u_points", JSON.stringify(data));

    // Update user
    this.system.user.points = data.balance;
    this.system.user.tier = data.tier;

    // Show notification
    this.system.showNotification(`You earned ${points} points!`, "success");

    return data;
  }

  async redeem(points, rewardId) {
    const data = await this.getBalance();

    if (data.balance < points) {
      throw new Error("Insufficient points");
    }

    data.balance -= points;

    // Add to history
    data.history = data.history || [];
    data.history.unshift({
      type: "redeemed",
      points,
      rewardId,
      timestamp: new Date().toISOString(),
    });

    localStorage.setItem("hallyu4u_points", JSON.stringify(data));

    // Update user
    this.system.user.points = data.balance;

    return data;
  }

  calculateTier(lifetime) {
    if (lifetime >= 10000) return "diamond";
    if (lifetime >= 5000) return "platinum";
    if (lifetime >= 2000) return "gold";
    if (lifetime >= 1000) return "silver";
    return "bronze";
  }
}

// ===============================
// LANGUAGE MODULE
// ===============================

class LanguageModule {
  constructor(system) {
    this.system = system;
    this.currentLanguage = localStorage.getItem("hallyu4u_language") || "en";
    this.translations = {};
    this.loadTranslations();
  }

  loadTranslations() {
    // Load translations from locale files
    this.translations = {
      en: {
        welcome: "Welcome to Hallyu4U",
        dashboard: "Dashboard",
        warehouse: "Warehouse",
        shipping: "Shipping",
        tracking: "Tracking",
      },
      ko: {
        welcome: "Hallyu4U에 오신 것을 환영합니다",
        dashboard: "대시보드",
        warehouse: "창고",
        shipping: "배송",
        tracking: "추적",
      },
      ja: {
        welcome: "Hallyu4Uへようこそ",
        dashboard: "ダッシュボード",
        warehouse: "倉庫",
        shipping: "配送",
        tracking: "追跡",
      },
      "zh-CN": {
        welcome: "欢迎来到Hallyu4U",
        dashboard: "仪表板",
        warehouse: "仓库",
        shipping: "运输",
        tracking: "追踪",
      },
    };
  }

  setLanguage(lang) {
    this.currentLanguage = lang;
    localStorage.setItem("hallyu4u_language", lang);
    this.updateUI();
  }

  t(key) {
    return (
      this.translations[this.currentLanguage]?.[key] ||
      this.translations.en[key] ||
      key
    );
  }

  updateUI() {
    // Update all elements with data-translate attribute
    document.querySelectorAll("[data-translate]").forEach((element) => {
      const key = element.dataset.translate;
      element.textContent = this.t(key);
    });
  }
}

// ===============================
// INITIALIZE SYSTEM
// ===============================

// Create global instance
window.hallyu4u = new Hallyu4UCompleteSystem();

// Export for use in other scripts
window.Hallyu4USystem = Hallyu4UCompleteSystem;
